import { ShieldCheck, Crown, Plane, Users, Building2, ArrowDown } from "lucide-react";
import { HIERARCHY } from "../mock";
import { useReveal } from "../hooks/useReveal";

const ICONS = { ShieldCheck, Crown, Plane, Users, Building2 };

const Tier = ({ t, idx, last }) => {
  const Icon = ICONS[t.icon] || Plane;
  const ref = useReveal();
  return (
    <div ref={ref} className="reveal" style={{ transitionDelay: `${idx * 80}ms` }}>
      <div
        data-testid={`hierarchy-tier-${t.tier}`}
        className="group relative bg-[#121A33]/50 backdrop-blur-sm border border-[#2A3451] hover:border-[#C19A5D]/60 transition-all duration-500 p-6 sm:p-8"
      >
        {/* Left tier rail */}
        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#C19A5D]/30 group-hover:bg-[#C19A5D] transition-colors duration-500" />

        <div className="grid grid-cols-12 gap-4 sm:gap-6 items-start">
          {/* Tier badge + Icon */}
          <div className="col-span-12 sm:col-span-3 lg:col-span-2 flex sm:block items-center gap-4">
            <div className="flex items-center gap-3 sm:flex-col sm:items-start">
              <span className="overline text-[10px] sm:text-xs">TIER {String(t.tier).padStart(2, "0")}</span>
              <div className="h-12 w-12 sm:mt-3 border border-[#C19A5D]/40 bg-[#0A1128] flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-[#C19A5D]" strokeWidth={1.25} />
              </div>
            </div>
          </div>

          {/* Name + summary */}
          <div className="col-span-12 sm:col-span-9 lg:col-span-7">
            <div className="text-xs text-white/45 uppercase tracking-[0.2em] font-medium mb-1.5">
              {t.overline}
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
              {t.name}
            </h3>
            <p className="mt-3 text-white/65 leading-relaxed text-sm sm:text-base">
              {t.summary}
            </p>
          </div>

          {/* Capabilities */}
          <div className="col-span-12 lg:col-span-3 lg:border-l lg:border-white/10 lg:pl-6">
            <div className="overline text-[10px] mb-3">Capabilities</div>
            <ul className="space-y-2">
              {t.capabilities.map((c, i) => (
                <li key={i} className="text-xs sm:text-sm text-white/75 flex items-start gap-2">
                  <span className="text-[#C19A5D] mt-1 h-1 w-1 rounded-full bg-[#C19A5D] shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Connector */}
      {!last && (
        <div className="flex justify-start pl-6 sm:pl-10 py-1" aria-hidden>
          <ArrowDown className="h-4 w-4 text-[#C19A5D]/60" strokeWidth={1.5} />
        </div>
      )}
    </div>
  );
};

export const Hierarchy = () => {
  const ref = useReveal();
  return (
    <section
      id="hierarchy"
      data-testid="hierarchy-section"
      className="relative py-28 lg:py-32 bg-[#0A1128]/40 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div ref={ref} className="reveal max-w-3xl mb-16">
          <span className="overline">Multi-tier architecture</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            Five command layers.
            <br />
            <span className="gold-text">One chain of accountability.</span>
          </h2>
          <p className="mt-6 text-white/65 text-lg leading-relaxed">
            OmniLink's ECP architecture mirrors how aviation actually operates — from independent
            Controllers down to Department Teams. Every tier has the workspace it needs, every
            action is attributable, and the audit trail never lies.
          </p>
        </div>

        <div className="space-y-0">
          {HIERARCHY.map((t, i) => (
            <Tier key={t.tier} t={t} idx={i} last={i === HIERARCHY.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
};
